import { useWrapRequest } from '@components/hooks/use-wrap-request';
import { userSearchDebounce } from '@components/hooks/use-search';
import { useSearchReducer } from '@components/hooks/useSearchReducer';
import { useError } from '@components/hooks/useError';
import {
    CellProps,
    ReactColumn,
    SortingRule,
    TableComponent
} from '@components/table';
import { Button } from '@components/ui/button';
import { TeamMemberRole, TeamMemberSupportDto } from '@api/ChallengeClient';
import { Api } from '@core/api';
import { links } from '@core/router';
import { injectTSDI } from '@core/tsdi';
import { Badge } from '@components/ui/badge';
import { Download } from 'lucide-react';
import { debounce } from 'lodash';
import { format, parseISO } from 'date-fns';
import { EditJoinDayDialog } from '@components/edit-join-day-dialog';
import React from 'react';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import { TeamContext } from '..';

interface PageResponse<T> {
    content: T[];
    number: number;
    size: number;
    totalPages: number;
    totalElements: number;
}

function createEmptyPage<T>(): PageResponse<T> {
    return {
        content: [],
        number: 0,
        size: 30,
        totalPages: 0,
        totalElements: 0
    };
}

type TeamMemberListEntry = TeamMemberSupportDto;

function useTeamMembersStore(teamId: number) {
    const { challengeClient } = injectTSDI(Api);
    const [sorting, setSorting] = React.useState<
        SortingRule | SortingRule[] | undefined
    >(undefined);

    const [searchParams, dispatch] = React.useReducer(useSearchReducer, {
        size: 30,
        page: 0,
        sort: '-createdDate'
    });
    const { setSearchTerm } = userSearchDebounce(dispatch);

    React.useEffect(() => {
        if (sorting) {
            dispatch({ type: 'sort', sort: sorting });
        }
    }, [sorting]);

    const teamMembers = useWrapRequest(
        async (params?: QueryParamsDto) => {
            const response =
                await challengeClient.support.teamSupportControllerGetMembers(
                    teamId,
                    {
                        page: params?.page ?? 0,
                        size: params?.size ?? 30,
                        search: params?.search,
                        sort: params?.sort ?? '-createdDate'
                    }
                );

            return {
                ...response.data,
                content: (response.data.content || []) as TeamMemberListEntry[]
            };
        },
        {
            defaultData: createEmptyPage<TeamMemberListEntry>()
        }
    );

    const debouncedRequest = React.useMemo(
        () =>
            debounce((params?: QueryParamsDto) => {
                teamMembers.request([params]);
            }, 100),
        [teamMembers]
    );

    React.useEffect(() => {
        debouncedRequest(searchParams);
        return () => {
            debouncedRequest.cancel();
        };
    }, [debouncedRequest, searchParams]);

    const updateMemberJoinDay = React.useCallback(
        async (teamMemberId: number, joinDay: string) => {
            await challengeClient.support.teamSupportControllerUpdateMemberJoinDay(
                teamId,
                teamMemberId,
                { joinDay }
            );
            teamMembers.request([searchParams], { stateLoading: false });
        },
        [challengeClient, teamId, teamMembers, searchParams]
    );

    const updateMemberRole = React.useCallback(
        async (teamMemberId: number, role: TeamMemberRole) => {
            await challengeClient.support.teamSupportControllerUpdateMemberRole(
                teamId,
                teamMemberId,
                { role }
            );
            teamMembers.request([searchParams], { stateLoading: false });
        },
        [challengeClient, teamId, teamMembers, searchParams]
    );

    return {
        dispatch,
        searchParams,
        setSearchTerm,
        setSorting,
        sorting,
        teamMembers,
        updateMemberJoinDay,
        updateMemberRole
    };
}

const TeamMembers = observer(() => {
    const { googleUserAuth, gatewayApiUrl } = injectTSDI(Api);
    const showError = useError();
    const [exporting, setExporting] = React.useState(false);
    const { team } = React.useContext(TeamContext);
    const {
        dispatch,
        searchParams,
        setSearchTerm,
        setSorting,
        sorting,
        teamMembers,
        updateMemberJoinDay,
        updateMemberRole
    } = useTeamMembersStore(team.id);

    const [joinDayMember, setJoinDayMember] =
        React.useState<TeamMemberListEntry | null>(null);

    const onExport = async () => {
        setExporting(true);
        try {
            const response = await fetch(
                `${gatewayApiUrl('challenge-api')}/support/v1/teams/${team.id}/members/export${
                    searchParams.search
                        ? `?${new URLSearchParams({ search: searchParams.search })}`
                        : ''
                }`,
                {
                    headers: {
                        authorization: `Bearer ${googleUserAuth?.accessToken}`
                    }
                }
            );
            const blob = await response.blob();
            const data = new Blob([blob], { type: 'text/csv' });
            const csvURL = window.URL.createObjectURL(data);
            const tempLink = document.createElement('a');
            tempLink.href = csvURL;
            tempLink.setAttribute(
                'download',
                `team-members-${team.id}-${format(new Date(), 'yyyy-MM-dd')}.csv`
            );
            tempLink.click();
        } catch (e) {
            showError(e);
        } finally {
            setExporting(false);
        }
    };

    const onSortingChange = (value?: SortingRule) => {
        if (value) {
            setSorting(value);
        }
    };

    const columns: ReactColumn<TeamMemberListEntry>[] = [
        {
            Header: 'Name',
            accessor: 'name',
            style: { width: 240 },
            Cell: ({
                row: {
                    original: { name, userId }
                }
            }: CellProps<TeamMemberListEntry, string>) => (
                <Link
                    to={links.userDetails(userId)}
                    className="text-sm font-semibold hover:underline"
                >
                    {name}
                </Link>
            )
        },
        {
            Header: 'User ID',
            accessor: 'userId',
            style: { width: 280 },
            Cell: ({ value }: CellProps<TeamMemberListEntry, string>) => (
                <Link
                    to={links.userDetails(value)}
                    className="text-sm font-semibold hover:underline"
                >
                    {value}
                </Link>
            )
        },
        {
            Header: 'Role',
            accessor: 'role',
            style: { width: 180 },
            Cell: ({
                value
            }: CellProps<TeamMemberListEntry, TeamMemberRole>) => (
                <Badge
                    variant={
                        value === TeamMemberRole.MANAGER ? 'default' : 'outline'
                    }
                >
                    {value}
                </Badge>
            )
        },
        {
            Header: 'Joined Date',
            accessor: 'joinDay',
            style: { width: 180 },
            Cell: ({ value }: CellProps<TeamMemberListEntry, string>) => (
                <span className="text-sm">
                    {format(parseISO(value), 'dd.MM.yy')}
                </span>
            )
        }
    ];

    return (
        <>
            <div className="mb-4 flex justify-end">
                <Button
                    variant="outline"
                    size="sm"
                    loading={exporting}
                    disabled={exporting}
                    onClick={onExport}
                >
                    <Download />
                    Export CSV
                </Button>
            </div>
            <TableComponent<TeamMemberListEntry[]>
                columns={columns}
                emptyText="No team members found"
                items={teamMembers}
                searchPlaceholder="Search by name or user ID..."
                tableLayout="auto"
                onSortingChange={onSortingChange}
                sorting={Array.isArray(sorting) ? sorting[0] : sorting}
                onSearchChange={setSearchTerm}
                onChangePage={(page) => dispatch({ type: 'page', page })}
                onPageSizeChange={(size) => dispatch({ type: 'size', size })}
                contextMenu={[
                    {
                        label: (member: TeamMemberListEntry) =>
                            member.role === TeamMemberRole.MANAGER
                                ? 'Make Member'
                                : 'Make Manager',
                        action: async (member: TeamMemberListEntry) => {
                            try {
                                await updateMemberRole(
                                    member.id,
                                    member.role === TeamMemberRole.MANAGER
                                        ? TeamMemberRole.MEMBER
                                        : TeamMemberRole.MANAGER
                                );
                            } catch (e) {
                                showError(e);
                            }
                        }
                    },
                    {
                        label: () => 'Edit Join Date',
                        action: (member: TeamMemberListEntry) =>
                            setJoinDayMember(member)
                    }
                ]}
            />
            <EditJoinDayDialog
                open={Boolean(joinDayMember)}
                joinDay={joinDayMember?.joinDay}
                onClose={() => setJoinDayMember(null)}
                onSubmit={async (joinDay) => {
                    try {
                        await updateMemberJoinDay(joinDayMember!.id, joinDay);
                        setJoinDayMember(null);
                    } catch (e) {
                        showError(e);
                    }
                }}
            />
        </>
    );
});

export default TeamMembers;
